<template>
    <div id="competitions">
        <!-- HERO -->
        <section class="comp-hero">
            <div class="hero-media">
                <video autoplay loop muted playsinline class="hero-video hero-video-fill">
                    <source src="@/assets/video/competitions.mp4" type="video/mp4" />
                </video>
                <div class="hero-grain"></div>
                <div class="hero-overlay"></div>
            </div>

            <div class="hero-top">
                <span class="hero-brand">AGH SOLAR BOAT</span>
                <span class="hero-divider"></span>
                <span class="hero-tag">{{ $t('competitions.page.tag') }}</span>
            </div>

            <div class="hero-content">
                <span class="hero-eyebrow">{{ $t('competitions.page.eyebrow') }}</span>
                <h1 class="hero-title">{{ $t('competitions.line_1') }}</h1>
                <p class="hero-lead">{{ $t('competitions.line_2') }}</p>
            </div>

            <div class="hero-bottom">
                <a href="#map" class="scroll-link">
                    <span>{{ $t('competitions.page.scroll_map') }}</span>
                    <span class="scroll-arrow">↓</span>
                </a>
            </div>
        </section>

        <!-- MAP / NAVIGATION -->
        <section id="map" class="block-light">
            <v-container class="block-container">
                <div class="section-meta">
                    <span class="chapter-num">01</span>
                    <span class="chapter-divider"></span>
                    <span class="chapter-eyebrow">{{ $t('competitions.page.map_eyebrow') }}</span>
                </div>

                <h2 class="block-title" v-html="$t('competitions.page.map_title')"></h2>

                <div class="map-grid">
                    <a
                        v-for="(comp, i) in competitions"
                        :key="comp.id"
                        :href="'#' + comp.id"
                        class="map-card">
                        <span class="map-num">{{ String(i + 1).padStart(2, '0') }}</span>
                        <div class="map-info">
                            <span class="map-country">{{ $t(comp.key + '.country') }}</span>
                            <span class="map-city">{{ $t(comp.key + '.name') }}</span>
                            <span class="map-location">{{ $t(comp.key + '.city') }}</span>
                        </div>
                        <span class="map-arrow">↓</span>
                    </a>
                </div>
            </v-container>
        </section>

        <!-- COMPETITION DEEP DIVES -->
        <section
            v-for="(comp, i) in competitions"
            :key="comp.id"
            :id="comp.id"
            :class="['comp-block', i % 2 === 0 ? 'block-light' : 'block-dark']">
            <v-container class="block-container">
                <div :class="['section-meta', i % 2 === 1 && 'section-meta--light']">
                    <span class="chapter-num">{{ String(i + 1).padStart(2, '0') }}</span>
                    <span class="chapter-divider"></span>
                    <span class="chapter-eyebrow">{{ $t(comp.key + '.city') }} · {{ $t(comp.key + '.country') }}</span>
                </div>

                <div class="comp-grid" :class="{ 'is-flipped': i % 2 === 1 }">
                    <div :class="['comp-media', comp.ksafWatermark && 'comp-media--ksaf']">
                        <img :src="comp.image" :alt="$t(comp.key + '.name')" />
                    </div>

                    <div class="comp-text">
                        <h3
                            class="comp-title"
                            :class="{ 'comp-title--light': i % 2 === 1 }">
                            {{ $t(comp.key + '.name') }}
                        </h3>
                        <p
                            class="comp-intro"
                            :class="{ 'comp-intro--light': i % 2 === 1 }">
                            {{ $t(comp.key + '.a') }}
                        </p>
                        <p
                            class="comp-body"
                            :class="{ 'comp-body--light': i % 2 === 1 }">
                            {{ $t(comp.key + '.b') }}
                        </p>
                    </div>
                </div>
            </v-container>
        </section>
    </div>
</template>

<script>
    export default {
        name: "ContentComponent",
        data() {
            return {
                competitions: [
                    {
                        id: 'monte-carlo',
                        key: 'competitions.monte-carlo',
                        image: require('@/assets/img/components/competitions/monako/20220709_KSAF_AGH_AGHSolarBoatMonacoEnergyBoatChallenge_KSadowy_013.webp'),
                        ksafWatermark: true
                    },
                    {
                        id: 'usa',
                        key: 'competitions.usa',
                        image: require('@/assets/img/components/competitions/usa.webp')
                    },
                    {
                        id: 'akkrum',
                        key: 'competitions.akkrum',
                        image: require('@/assets/img/components/competitions/akk.webp'),
                        ksafWatermark: true
                    },
                    {
                        id: 'wildau',
                        key: 'competitions.wildau',
                        image: require('@/assets/img/components/competitions/germanzzzz.webp'),
                        ksafWatermark: true
                    },
                    {
                        id: 'balaton',
                        key: 'competitions.balaton',
                        image: require('@/assets/img/components/competitions/hungary.webp'),
                        ksafWatermark: true
                    },
                    {
                        id: 'sardynia',
                        key: 'competitions.sardynia',
                        image: require('@/assets/img/components/competitions/sardynia.webp'),
                        ksafWatermark: true
                    },
                    {
                        id: 'reguengos',
                        key: 'competitions.reguengos',
                        image: require('@/assets/img/components/competitions/portugalia/20210604_KSAF_AGH_SolarBoatZawody2021_MSzmigiel_021.webp'),
                        ksafWatermark: true
                    },
                    {
                        id: 'enkuizen',
                        key: 'competitions.enkuizen',
                        image: require('@/assets/img/components/competitions/enkuizen.webp')
                    }
                ]
            }
        }
    }
</script>

<style lang="sass" scoped>
    #competitions
        background: #faf7f1
        color: #0a0828
        font-family: Inter, sans-serif

    /* === HERO === */
    .comp-hero
        position: relative
        width: 100%
        height: 100vh
        min-height: 720px
        overflow: hidden
        background: #0a0828
        color: #fff
        display: flex
        flex-direction: column
        justify-content: space-between
        padding: 110px 0 50px

    .hero-media
        position: absolute
        inset: 0
        z-index: 0
        overflow: hidden
        background: #0a0828

        .hero-video
            position: absolute
            top: 50%
            left: 50%
            min-width: 100%
            min-height: 100%
            width: auto
            height: auto
            max-width: none
            transform: translate(-50%, -50%)
            object-fit: cover
            display: block
            filter: blur(2px) saturate(1.1) brightness(0.7) contrast(1.05)

        .hero-grain
            position: absolute
            inset: 0
            opacity: 0.18
            mix-blend-mode: overlay
            pointer-events: none
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>")
            background-repeat: repeat
            background-size: 200px 200px

        .hero-overlay
            position: absolute
            inset: 0
            background: linear-gradient(180deg, rgba(10, 8, 40, 0.45) 0%, rgba(10, 8, 40, 0.25) 35%, rgba(10, 8, 40, 0.85) 100%)

    .hero-top
        position: relative
        z-index: 2
        display: flex
        align-items: center
        gap: 18px
        padding: 0 56px
        font-size: 12px
        font-weight: 500
        letter-spacing: 0.18em
        text-transform: uppercase
        color: rgba(255, 255, 255, 0.85)

        .hero-divider
            width: 28px
            height: 1px
            background: rgba(255, 255, 255, 0.5)

    .hero-content
        position: relative
        z-index: 2
        max-width: 1280px
        margin: 0 auto
        padding: 0 56px
        width: 100%

        .hero-eyebrow
            display: inline-block
            font-size: 12px
            font-weight: 500
            letter-spacing: 0.24em
            text-transform: uppercase
            color: rgba(255, 255, 255, 0.7)
            margin-bottom: 28px

        .hero-title
            font-family: Raleway, sans-serif
            font-weight: 900
            font-size: clamp(48px, 7.5vw, 110px)
            line-height: 1
            letter-spacing: -0.025em
            margin: 0 0 32px
            color: #fff
            text-transform: lowercase

        .hero-lead
            font-size: clamp(16px, 1.5vw, 19px)
            line-height: 1.6
            font-weight: 400
            color: rgba(255, 255, 255, 0.88)
            max-width: 720px
            margin: 0
            text-indent: 0

    .hero-bottom
        position: relative
        z-index: 2
        display: flex
        justify-content: flex-end
        padding: 0 56px
        max-width: 1280px
        margin: 0 auto
        width: 100%

        .scroll-link
            display: inline-flex
            align-items: center
            gap: 12px
            font-size: 12px
            font-weight: 500
            letter-spacing: 0.18em
            text-transform: uppercase
            color: rgba(255, 255, 255, 0.85)
            text-decoration: none
            padding: 14px 22px
            border: 1px solid rgba(255, 255, 255, 0.4)
            border-radius: 999px
            transition: all 0.2s ease

            &:hover
                background: rgba(255, 255, 255, 0.08)
                border-color: rgba(255, 255, 255, 0.7)

    /* === BLOCK BASE === */
    .block-light
        background: #faf7f1
        color: #0a0828
        padding: 140px 0

    .block-dark
        background: #0a0828
        color: #fff
        padding: 140px 0

    .block-container
        max-width: 1280px !important
        padding: 0 56px !important

    .section-meta
        display: flex
        align-items: center
        gap: 18px
        margin-bottom: 36px
        font-size: 12px
        font-weight: 500
        letter-spacing: 0.22em
        text-transform: uppercase
        color: #0a0828

        .chapter-num
            font-family: Raleway, sans-serif
            font-weight: 700
            font-size: 14px
            letter-spacing: 0.1em

        .chapter-divider
            width: 36px
            height: 1px
            background: #0a0828
            opacity: 0.4

        .chapter-eyebrow
            opacity: 0.85

        &.section-meta--light
            color: #fff

            .chapter-divider
                background: #fff
                opacity: 0.5

    .block-title
        font-family: Raleway, sans-serif
        font-weight: 900
        font-size: clamp(40px, 6vw, 88px)
        line-height: 0.98
        letter-spacing: -0.025em
        margin: 0 0 80px
        color: #0a0828
        text-transform: lowercase

    /* === MAP GRID === */
    .map-grid
        display: grid
        grid-template-columns: repeat(2, 1fr)
        column-gap: 48px

    .map-card
        display: grid
        grid-template-columns: 64px 1fr 24px
        align-items: center
        gap: 20px
        padding: 28px 0
        border-bottom: 1px solid rgba(10, 8, 40, 0.15)
        text-decoration: none
        color: #0a0828
        transition: all 0.2s ease

        &:hover
            padding-left: 12px
            border-color: rgba(10, 8, 40, 0.45)

            .map-arrow
                transform: translateY(4px)

        .map-num
            font-family: Raleway, sans-serif
            font-weight: 700
            font-size: 13px
            letter-spacing: 0.15em
            color: rgba(10, 8, 40, 0.45)

        .map-info
            display: flex
            flex-direction: column
            gap: 4px

            .map-country
                font-size: 11px
                font-weight: 500
                letter-spacing: 0.22em
                text-transform: uppercase
                color: rgba(10, 8, 40, 0.6)

            .map-city
                font-family: Raleway, sans-serif
                font-weight: 800
                font-size: clamp(18px, 1.8vw, 26px)
                line-height: 1.15
                letter-spacing: -0.015em
                color: #0a0828

            .map-location
                font-size: 13px
                font-weight: 500
                letter-spacing: 0.04em
                color: rgba(10, 8, 40, 0.55)

        .map-arrow
            font-size: 18px
            color: rgba(10, 8, 40, 0.55)
            transition: transform 0.2s ease
            text-align: right

    /* === COMPETITION BLOCKS === */
    .comp-grid
        display: grid
        grid-template-columns: 6fr 6fr
        gap: 80px
        align-items: center

        &.is-flipped
            .comp-media
                order: 2

            .comp-text
                order: 1

    .comp-media
        position: relative
        overflow: hidden
        border-radius: 4px

        img
            width: 100%
            max-width: 100%
            aspect-ratio: 4 / 3
            object-fit: cover
            object-position: center
            display: block

        &--ksaf img
            aspect-ratio: unset
            max-height: min(75vw, 420px)
            height: auto
            object-fit: contain

    .comp-text
        display: flex
        flex-direction: column
        gap: 24px

        .comp-title
            font-family: Raleway, sans-serif
            font-weight: 900
            font-size: clamp(28px, 3.6vw, 52px)
            line-height: 1.05
            letter-spacing: -0.025em
            margin: 0
            color: #0a0828
            text-transform: none

            &.comp-title--light
                color: #fff

        .comp-intro
            font-size: clamp(16px, 1.2vw, 18px)
            line-height: 1.6
            font-weight: 500
            color: rgba(10, 8, 40, 0.92)
            margin: 0
            text-indent: 0

            &.comp-intro--light
                color: rgba(255, 255, 255, 0.95)

        .comp-body
            font-size: clamp(14px, 1vw, 16px)
            line-height: 1.75
            color: rgba(10, 8, 40, 0.72)
            margin: 0
            text-indent: 0

            &.comp-body--light
                color: rgba(255, 255, 255, 0.78)

    /* === RESPONSIVE === */
    @media (max-width: 960px)
        .comp-hero
            min-height: 640px
            padding: 100px 0 30px

        .hero-top
            padding: 0 28px
            font-size: 11px
            gap: 14px

            .hero-divider--hide
            .hero-since--hide
                display: none

        .hero-content
            padding: 0 28px

            .hero-title
                font-size: clamp(40px, 11vw, 68px)

        .hero-bottom
            padding: 0 28px
            justify-content: center

        .block-light, .block-dark
            padding: 90px 0

        .block-container
            padding: 0 28px !important

        .block-title
            margin-bottom: 56px

        .map-grid
            grid-template-columns: 1fr

        .map-card
            grid-template-columns: 48px 1fr 20px
            padding: 22px 0

            .map-info .map-city
                font-size: 22px

        .comp-grid
            grid-template-columns: 1fr
            gap: 36px

            &.is-flipped
                .comp-media
                    order: 1

                .comp-text
                    order: 2

        .comp-media img
            max-height: min(62vw, 320px)

        .comp-media--ksaf img
            max-height: min(78vw, 360px)

</style>
